import { VisualMatch } from '@dupe/common/types'
import { useMemo } from 'react'
import useGetDupes, {
  type DupeInputType,
  GetDupesResponse,
  UseGetDupesReturn,
} from '~/src/hooks/useGetDupes'
import useGetDupesQuick, {
  GetDupesQuickResponse,
} from '~/src/hooks/useGetDupesQuick'

export type UseGetDupesTwoPhaseReturn = {
  /** Combined matches - shows quick results first, then full results when available */
  matches: VisualMatch[]
  /** Visual match ID from the response */
  visualMatchId: string | undefined
  /** Server-resolved ShoppingCategory for these results (furniture-inclusive),
   * or null. Prefers full response, falls back to quick. Lets seed/instant-paint
   * callers classify a turn as revenue-protected the same way a streamed turn
   * is — originally to gate the sponsored picks rail v2, deleted 2026-09-16. */
  category: string | null
  /** True while waiting for quick results (initial loading) */
  isLoadingQuick: boolean
  /** True while waiting for full results (background loading) */
  isLoadingFull: boolean
  /** True while loading is in progress and no data has arrived yet */
  isLoading: boolean
  /** True if we have quick results but are still waiting for full results */
  isLoadingMore: boolean
  /** True if current results are from quick endpoint (partial) */
  isPartial: boolean
  /** True if results came from cache */
  cached: boolean
  /** Server-derived input modality (`url` | `image` | `null`). Prefers full
   * response, falls back to quick. Resolved from the stored search row's
   * `results.source`, so hash-only continuations inherit the originating
   * search's modality. Threaded to leaf cards (via `ResultSortProvider`) to
   * stamp `input_type` on `Result Viewed` / `Dupe Result Clicked` for
   * per-modality CTR. */
  inputType: DupeInputType
  /** Error from either request */
  error: Error | null
  /** Quick response data */
  quickData: GetDupesQuickResponse | undefined
  /** Full response data */
  fullData: GetDupesResponse | undefined
  /** Refresh function from useGetDupes */
  refresh: UseGetDupesReturn['refresh']
  /** Has the FULL phase's query ever resolved (data or error)? See
   *  `UseGetDupesReturn.isFetched` — the only honest "it ran" signal. */
  fullFetched: UseGetDupesReturn['isFetched']
  /** Sources that contributed to current results */
  sources: {
    width_ai: boolean
    ebay: boolean
    google_lens: boolean
    shopify: boolean
  }
  // Backward compatibility with UseGetDupesReturn
  /** @deprecated Use matches directly. Provided for backward compatibility. */
  data: { matches: VisualMatch[]; visualMatchId: string | undefined }
  /** @deprecated Use isLoadingFull. Provided for backward compatibility. */
  refreshing: boolean
  /** @deprecated Use isLoadingFull. Provided for backward compatibility. */
  isValidating: boolean
}

type UseGetDupesTwoPhaseOptions = {
  hash?: string
  rectHash?: string
  selectedCountry?: string
  noCache?: boolean
  q?: string
  productCategory?: string
  threadId?: string
  limit?: number
  includeEbay?: boolean
  /** Forwarded to `useGetDupes`: fire `partner_pin_exposure` from this
   *  surface. Only the measured `/[hash]` grid opts in. */
  trackPartnerPinExposure?: boolean
  /**
   * If true, skip the quick endpoint and only use full endpoint.
   * Useful when you know you need all sources.
   * @default false
   */
  skipQuick?: boolean
  /**
   * Gate both phases. Useful for deferring the fetch until inputs that key
   * the underlying queries (e.g. PostHog flag variants) have settled —
   * firing before they resolve changes the query key mid-flight and
   * duplicates the (expensive) server-side search.
   * @default true
   */
  enabled?: boolean
  /**
   * Forwarded to `useGetDupes` (the FULL phase only): search even when the
   * browser never worked out a country, letting the server resolve the market
   * from the request IP.
   *
   * ⚠️ Deliberately not forwarded to the quick phase. Quick exists to paint
   * something fast on a warm cache; the caller that opts in here is the seed,
   * which sets `skipQuick: true` anyway, so wiring it to both would add an
   * untested path for no gain.
   * @default false
   */
  allowMissingCountry?: boolean
}

/**
 * Two-phase loading hook for dupes that provides fast initial results
 * from internal sources (Width AI + eBay) while loading complete results
 * from all sources (including Google Lens, Shopify) in the background.
 *
 * @example
 * ```tsx
 * const {
 *   matches,
 *   isLoading,
 *   isLoadingMore,
 *   isPartial,
 * } = useGetDupesTwoPhase(image, { hash, selectedCountry })
 *
 * // Show results immediately (from quick endpoint ~200ms)
 * // isLoadingMore will be true while full results load in background
 * // matches will update automatically when full results arrive
 * ```
 */
const useGetDupesTwoPhase = (
  image: string | null | undefined,
  options: UseGetDupesTwoPhaseOptions = {}
): UseGetDupesTwoPhaseReturn => {
  const {
    hash,
    rectHash,
    selectedCountry,
    noCache,
    q,
    productCategory,
    threadId,
    limit,
    includeEbay,
    skipQuick = false,
    enabled = true,
    trackPartnerPinExposure = false,
    allowMissingCountry = false,
  } = options

  // Quick endpoint - fast internal sources only
  const {
    data: quickData,
    isLoading: isLoadingQuick,
    error: quickError,
  } = useGetDupesQuick(image, {
    hash,
    rectHash,
    selectedCountry,
    noCache,
    q,
    productCategory,
    limit,
    includeEbay,
    enabled: enabled && !skipQuick,
  })

  // Full endpoint - all sources including slow external APIs
  const {
    data: fullData,
    isLoading: isLoadingFull,
    isValidating: isValidatingFull,
    error: fullError,
    refresh,
    isFetched: fullFetched,
  } = useGetDupes(image, {
    hash,
    rectHash,
    selectedCountry,
    noCache,
    q,
    productCategory,
    threadId,
    limit,
    includeEbay,
    enabled,
    trackPartnerPinExposure,
    allowMissingCountry,
  })

  // Derive combined state
  const result = useMemo((): UseGetDupesTwoPhaseReturn => {
    // Prefer full data when available, fall back to quick data
    const hasFullData = !!fullData?.matches?.length
    const hasQuickData = !!quickData?.matches?.length

    // Use full data if available, otherwise use quick data
    const matches = hasFullData
      ? fullData.matches
      : hasQuickData
        ? quickData.matches
        : []

    const visualMatchId = fullData?.visualMatchId ?? quickData?.visualMatchId

    // Server-resolved category (furniture-inclusive ShoppingCategory). Only
    // the full response classifies it (the quick endpoint omits it), so the
    // classification resolves once full data lands — matching when the category
    // is actually known. Surfaced so seed/instant-paint callers can mark a turn
    // revenue-protected the same way a streamed turn is (`extractRailCategory`
    // reads this exact field server-side). It gated the sponsored picks rail v2
    // until that rail was deleted on 2026-09-16.
    const category = fullData?.category ?? null

    // Loading states
    const isLoadingQuickPhase = !skipQuick && isLoadingQuick
    const isLoadingFullPhase = isLoadingFull || isValidatingFull
    const hasAnyData = hasFullData || hasQuickData
    // Still loading if we have no data and either phase is in progress
    const isLoading = !hasAnyData && (isLoadingQuickPhase || isLoadingFullPhase)
    const isLoadingMore = hasQuickData && !hasFullData && isLoadingFullPhase

    // Partial state - true if showing quick results only
    const isPartial = !hasFullData && hasQuickData
    const cached = fullData?.cached ?? quickData?.cached ?? false
    // Prefer the full response's modality, fall back to quick. Both endpoints
    // derive it identically server-side, so they agree; the `??` only bridges
    // the window before full data lands.
    const inputType = fullData?.inputType ?? quickData?.inputType ?? null

    // Sources - use actual source info from responses when available
    const sources = fullData?.sources ??
      quickData?.sources ?? {
        width_ai: false,
        ebay: false,
        google_lens: false,
        shopify: false,
      }

    return {
      matches,
      visualMatchId,
      category,
      isLoadingQuick: isLoadingQuickPhase,
      isLoadingFull: isLoadingFullPhase,
      isLoading,
      isLoadingMore,
      isPartial,
      cached,
      inputType,
      error: quickError ?? fullError,
      quickData,
      fullData,
      refresh,
      fullFetched,
      sources,
      // Backward compatibility with UseGetDupesReturn
      data: { matches, visualMatchId },
      refreshing: isLoadingFullPhase,
      isValidating: isLoadingFullPhase,
    }
  }, [
    quickData,
    fullData,
    isLoadingQuick,
    isLoadingFull,
    isValidatingFull,
    quickError,
    fullError,
    refresh,
    fullFetched,
    skipQuick,
  ])

  return result
}

export default useGetDupesTwoPhase
